/**
 * VS Code-style activity bar (Slack's left rail): one button per view (core Shell.SHELL_VIEWS — Chat, Email, Calendar, Contacts, Huddles, Docs, Files),
 * each swapping the sidebar's content. The view follows the active tab (Shell.followTab: a channel tab selects Chat,
 * a page selects Docs, neutral tabs leave it); a button only swaps the sidebar, and pressing the active one shows or
 * hides the sidebar. The last view is remembered per workspace. Phones (< 820px) have no bar: the sidebar drawer
 * shows the views as a segmented switch instead (SidebarViewSwitch).
 */
import { Shell, type MessageKey, type ShellView } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { IconButton, SegmentedControl, useT } from "@bundle/ui";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

/** One entry per view: icon and name. A new view is a SHELL_VIEWS entry in core plus a row here. */
export const VIEW_META: Record<ShellView, { icon: IconName; label: MessageKey }> = {
  chat: { icon: "message", label: "shell.view.chat" },
  email: { icon: "mail", label: "shell.view.email" },
  calendar: { icon: "calendar", label: "shell.view.calendar" },
  contacts: { icon: "contacts", label: "shell.view.contacts" },
  docs: { icon: "page", label: "shell.view.docs" },
  huddles: { icon: "huddle", label: "shell.view.huddles" },
  files: { icon: "folder", label: "drive.title" },
};

export type ViewBadge = { count?: number; dot?: boolean };

/** The sidebar's view and whether it shows (screens that duplicate the sidebar's navigation hide theirs). */
export const SidebarViewContext = createContext<{ view: ShellView; sidebarOpen: boolean } | null>(null);
export const useSidebarView = () => useContext(SidebarViewContext);

type Storage = { get(key: string): string | undefined; set(key: string, value: string): void };

/**
 * The selected view: restored per workspace, following the active tab when it changes (not on mount, so a reload
 * keeps the view I left). `available` hides views a workspace doesn't have (Email without the mail flag).
 */
export function useShellView(storage: Storage, wsId: string, activeUrl: string, available: readonly ShellView[]): [ShellView, (v: ShellView) => void] {
  const key = `shell.view.${wsId}`;
  // The stored choice stays even while its view is unavailable (mail registers after the shell mounts).
  const [chosen, setChosen] = useState<string | undefined>(() => storage.get(key));
  const view = Shell.restoreView(chosen, available);
  // A new active tab (click, ⌘1–9, back/forward, a link, split-pane focus): adjust during render, not in an effect.
  const [seenUrl, setSeenUrl] = useState(activeUrl);
  if (seenUrl !== activeUrl) {
    setSeenUrl(activeUrl);
    const next = Shell.followTab(view, activeUrl, available);
    if (next !== view) setChosen(next);
  }
  useEffect(() => {
    if (chosen !== undefined) storage.set(key, chosen);
  }, [storage, key, chosen]);
  const choose = useCallback((v: ShellView) => setChosen(v), []);
  return [view, choose];
}

type BarProps = {
  views: readonly ShellView[];
  view: ShellView;
  sidebarOpen: boolean;
  badges: Partial<Record<ShellView, ViewBadge>>;
  onSelect: (view: ShellView) => void;
  /** Pinned to the bottom of the bar (the Help button). */
  footer?: ReactNode;
};

export function ActivityBar({ views, view, sidebarOpen, badges, onSelect, footer }: BarProps) {
  const t = useT();
  return (
    <nav className="activity-bar" aria-label={t("shell.views")} data-testid="activity-bar">
      {views.map((v) => {
        const b = badges[v];
        const badge = b?.count !== undefined && b.count > 0 ? b.count : b?.dot === true ? true : undefined;
        const active = v === view;
        return (
          <div key={v} className="activity-item" data-active={active ? (sidebarOpen ? "open" : "closed") : undefined}>
            <IconButton
              icon={VIEW_META[v].icon}
              size="lg"
              accessibilityLabel={t(VIEW_META[v].label)}
              badge={badge}
              onPress={() => onSelect(v)}
              aria-pressed={active && sidebarOpen}
              testID={`activity-${v}`}
            />
          </div>
        );
      })}
      {footer}
    </nav>
  );
}

/** Phones: the views as a switch at the top of the sidebar drawer (CSS shows it below 820px only). */
export function SidebarViewSwitch({ views, view, onSelect }: { views: readonly ShellView[]; view: ShellView; onSelect: (view: ShellView) => void }) {
  const t = useT();
  if (views.length < 2) return null;
  return (
    <div className="sidebar-views">
      <SegmentedControl<ShellView>
        options={views.map((v) => ({ value: v, label: t(VIEW_META[v].label) }))}
        value={view}
        onChange={onSelect}
        accessibilityLabel={t("shell.views")}
        size="sm"
        fill
        testID="sidebar-views"
      />
    </div>
  );
}
